import { CommonModule } from '@angular/common';
import { Component, computed, effect, EventEmitter, inject, Input, model, Output, signal, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { StepsModule } from 'primeng/steps';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { SiteService } from '@/app/plateformebeneficiaire/core/service/site.service';
import { MeetingService } from '@/app/plateformebeneficiaire/core/service/meeting.service';
import { UserService } from '@/app/plateformebeneficiaire/core/service/user.service';
import { StepperModule } from 'primeng/stepper';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TenantOptionService } from '@/app/plateformebeneficiaire/core/service/tenantOption.service';
import { TenantOptionEnum } from '@/app/plateformebeneficiaire/core/models/enums/tenantOption.enum';
import { DomSanitizer } from '@angular/platform-browser';
import { InputTextModule } from 'primeng/inputtext';
import { Router } from '@angular/router';
import { TranslatePipe } from '@/app/layout/pipes/translate.pipe';

@Component({
  selector: 'app-create-meeting-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    StepsModule,
    ButtonModule,
    FormsModule,
    DialogModule,
    StepsModule,
    StepperModule,
    SelectModule,
    IftaLabelModule,
    TableModule,
    PanelModule,
    ToggleButtonModule,
    ProgressSpinnerModule,
    InputTextModule,
    TranslatePipe
  ],
  templateUrl: './create-meeting-dialog.component.html',
  styleUrl: './create-meeting-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateMeetingDialogComponent {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() reset = new EventEmitter<void>();
  @Output() created = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.resetState();
    this.reset.emit();
  }

  resetState() {
    this.siteId.set('');
    this.intervenantId.set('');
    this.typeRdvId.set('');
    this.horaire.set('');
    this.idsAgendasDisponibles.set([]);
    this.meetingCreated.set(false);
    this.meetingId = null;
    this.stepItem.set(1);
    this.clickCount.set(0);
    this.dateDebut.set(this.getFormattedDate(new Date()));
    this.dateFin.set(this.getFormattedDate(this.getDatePlusMonths(new Date(), 1)));
    this.createLoading = false;
    this.changeDetector.detectChanges();
  }

  constructor(
    private messageService: MessageService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private changeDetector: ChangeDetectorRef
  ) {
    effect(() => {
      const userInfo = this.#userInfoQuery.data();
      if (userInfo && userInfo.phoneNumber && !this.phoneNumber()) {
        this.phoneNumber.set(userInfo.phoneNumber);
      }
    });
  }

  
  siteId = model<string>('');
  intervenantId = model<string>('');
  typeRdvId = model<string>('');
  horaire = signal<string>('');
  phoneNumber = model<string>('');
  phoneNumberValid = computed(() => /^\d{10}$/.test(this.phoneNumber()));
  idsAgendasDisponibles = model<string[]>([]);

  meetingCreated = model<boolean>(false);

  dateDebut = signal<string>(this.getFormattedDate(new Date()));
  dateFin = signal<string>(
    this.getFormattedDate(this.getDatePlusMonths(new Date(), 1))
  );
  clickCount = signal<number>(0);

  meetingId: string | null = null;
  stepItem = signal(1);

  #siteService = inject(SiteService);
  #meetingService = inject(MeetingService);
  #tenantOptionService = inject(TenantOptionService);
  #userService = inject(UserService);

  #userInfoQuery = this.#userService.getUserInfos();
  #sitesQuery = this.#siteService.getSitesAvailables();
  #intervenantsQuery = this.#siteService.getSiteIntervenantsAvailables(
    this.siteId
  );
  #typesRDVQuery = this.#siteService.getTypesRDVAvailables(
    this.siteId,
    this.intervenantId
  );
  #disponibilitesQuery = this.#siteService.getDisponibilitesAvailables(
    this.siteId,
    this.intervenantId,
    this.typeRdvId,
    this.dateDebut,
    this.dateFin
  );

  sites = computed(() => this.#sitesQuery.data() ?? []);
  intervenants = computed(() => this.#intervenantsQuery.data() ?? []);
  typesRDV = computed(() => this.#typesRDVQuery.data() ?? []);
  disponibilites = computed(() => this.#disponibilitesQuery.data() ?? []);

  isLoadingSites = computed(() => this.#sitesQuery.isLoading());
  isLoadingIntervenants = computed(
    () => this.#intervenantsQuery.isLoading() ?? false
  );
  isLoadingTypesRDV = computed(() => this.#typesRDVQuery.isLoading() ?? false);
  isLoadingDisponibilites = computed(
    () => this.#disponibilitesQuery.isLoading() ?? false
  );
  canLoadMore = computed(() => this.clickCount() < 2);
  #tenantOptionsListQuery = this.#tenantOptionService.getListTenantOption();

  displayModal: boolean = false;
  createLoading: boolean = false;
  activeIndex = signal(0);
  steps = [
    { label: 'Vérification du téléphone' },
    { label: 'Sélection du site' },
    { label: "Sélection de l'intervenant" },
    { label: 'Date et heure' },
    { label: 'Validation' },
    { label: 'Rendez-vous validé' },
  ];
  // Redirige vers la 7ème étape (index 5)
  goToSuccessStep() {
    this.activeIndex.set(7);
  }


  navigateToPrepareMeetingAI(typeRDV: string, idMeeting: string, telBeneficiaire: string) {
    const encodedTypeRDV = btoa(unescape(encodeURIComponent(typeRDV)));
    const encodedIdMeeting = btoa(unescape(encodeURIComponent(idMeeting)));
    const encodedTelBeneficiaire = btoa(unescape(encodeURIComponent(telBeneficiaire)));
    this.closeDialog();
    this.router.navigate(
      ['/app/prepare-meeting-ai'], 
      { 
        queryParams: { 
          typeRDV: encodedTypeRDV ,
          idMeeting: encodedIdMeeting,
          telBeneficiaire: encodedTelBeneficiaire
        } 
      }
    );
  }

  intervenantDisplay = computed(() => {
    return (
      this.intervenants().find(
        (intervenant) => intervenant.id === this.intervenantId()
      )?.nom +
      ' ' +
      this.intervenants().find(
        (intervenant) => intervenant.id === this.intervenantId()
      )?.prenom +
      ' (' +
      this.intervenants().find(
        (intervenant) => intervenant.id === this.intervenantId()
      )?.mission +
      ')'
    );
  });
  typeRdvDisplay = computed(() => {
    return (
      this.typesRDV().find((typeRdv) => typeRdv.id === this.typeRdvId())
        ?.libelle +
      ' (' +
      this.typesRDV().find((typeRdv) => typeRdv.id === this.typeRdvId())
        ?.duree +
      ' minutes)'
    );
  });
  typeRdvIsVisio = computed(() => {
    return this.typesRDV().find((typeRdv) => typeRdv.id === this.typeRdvId())
      ?.visio === 'O';
  });
  siteDisplay = computed(() => {
    return this.sites().find((site) => site.id === this.siteId())?.libelle;
  });

  noAvailabilityMessage = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(
      this.getTenantOption(TenantOptionEnum.NoDisponibilitiesMessage)
    )
  );
  isMeetingIAEnabled = computed(() =>
    this.getTenantOption(TenantOptionEnum.MeetingIAEnabled) === 'true'
  )

  showModal() {
    this.displayModal = true;
  }

  hideModal() {
    this.displayModal = false;
  }

  goToStep(newStepNumber: number) {
    this.activeIndex.set(newStepNumber);
  }

  // Nouvelle étape : vérification du téléphone
  verifyPhoneNumber() {
    if (this.phoneNumberValid()) {
    }
  }
  chooseIntervenant(intervenantId: string): void {
    this.intervenantId.set(intervenantId);
  }
  chooseTypeRDV(typeRdvId: string): void {
    this.typeRdvId.set(typeRdvId);
  }
  chooseHoraire(jour: string, horaire: string, idsAgendasDisponibles: string[]): void {
    this.horaire.set(this.formatDateTimeForAPI(jour, horaire));
    this.idsAgendasDisponibles.set(idsAgendasDisponibles);
  }

  chooseSite(siteId: string) : void {
    this.siteId.set(siteId);
  }

  async siteExist(siteId: string): Promise<any> {
    while (this.isLoadingSites()) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    const siteselected = (this.#sitesQuery.data() ?? []).find(
      site => site.id === siteId
    );
    return siteselected;
  }

  async intervenantExist(intervenantId: string): Promise<any> {
    while (this.isLoadingIntervenants()) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    const intervenantSelected = (this.#intervenantsQuery.data() ?? []).find(
      inter => inter.id === intervenantId
    );
    return intervenantSelected;
  }

  async typeRdvExist(typeRdvId: string): Promise<any> {
    while (this.isLoadingTypesRDV()) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    const typeRdvSelected = (this.#typesRDVQuery.data() ?? []).find(
      typeRdv => typeRdv.id === typeRdvId
    );
    return typeRdvSelected;
  }

  validateMeeting(callbackFunction: any) {
    this.createLoading = true;
    this.#meetingService.createMeeting.mutate(
      {
        siteId: this.siteId(),
        intervenantId: this.intervenantId(),
        typeRdvId: this.typeRdvId(),
        horaire: this.horaire(),
        possiblesIntervenants : this.idsAgendasDisponibles(),
        visio: this.typeRdvIsVisio(),
        telephone: this.phoneNumber()
      },
      {
        onSuccess: (idMeeting: string) => {
          this.meetingId = idMeeting;
          this.createLoading = false;

          this.messageService.add({
            key: 'global',
            severity: 'success',
            summary: 'Rendez-vous validé !',
            detail: 'Votre rendez-vous a bien été créé !',
          });

          if (this.isMeetingIAEnabled()) {
            this.meetingCreated.set(true);
            callbackFunction(7);
          }
          else {
            this.created.emit();
          }
        },
        onError: (error: any) => {
          this.createLoading = false;
          this.displayModal = false;
          this.messageService.add({
            key: 'global',
            severity: 'error',
            summary: 'Erreur',
            detail:
              'Nous ne sommes pas parvenu a créer votre rendez-vous. Veuillez réessayer, si cela persiste, contactez notre secrétariat.',
          });
        },
      }
    );
  }

  updateMeeting() {
    if (this.meetingId) {
      this.createLoading = true;
      this.#meetingService.deleteMeeting.mutate({ meetingId: this.meetingId, skipNotification: true }, {
        onSuccess: () => {
          this.#meetingService.createMeeting.mutate({
            siteId: this.siteId(),
            intervenantId: this.intervenantId(),
            typeRdvId: this.typeRdvId(),
            horaire: this.horaire(),
            possiblesIntervenants: this.idsAgendasDisponibles(),
            visio: this.typeRdvIsVisio(),
            telephone: this.phoneNumber()
          }, {
            onSuccess: () => {
              this.createLoading = false;
              this.displayModal = false;
              this.messageService.add({ key: 'global', severity: 'success', summary: 'Success', detail: 'Rendez-vous modifié avec succès !' });
            },
            onError: (error) => {
              this.createLoading = false;
              this.displayModal = false;
              console.log('err', error)
              this.messageService.add({ key: 'global', severity: 'error', summary: 'Error', detail: 'erreur' });
            }
          });
        },
        onSettled: () => {
          this.meetingId = null;
          this.stepItem.set(1);
          this.closeDialog();
        },
        onError: (error) => {
          console.log('err', error)
          this.messageService.add({ key: 'global', severity: 'error', summary: 'Error', detail: 'erreur' });
        }
      });
    } else {
      console.error('Le meeting est introuvable');
    }
  }

  formatDateTimeForAPI(dateStr: string, timeStr: string): string {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date(year, month - 1, day, hours, minutes);
    return date.toISOString();
  }

  private getTenantOption(key: string): string {
    const options = this.#tenantOptionsListQuery.data() ?? [];
    return options.find((option) => option.key === key)?.value ?? '';
  }

  private getFormattedDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getDatePlusMonths(date: Date, months: number): Date {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + months);
    return newDate;
  }

  showMoreDisponibilities() {
    if (this.clickCount() < 2) {
      const newClickCount = this.clickCount() + 1;
      this.clickCount.set(newClickCount);

      const currentEndDate = this.getDatePlusMonths(
        new Date(),
        newClickCount + 1
      );
      this.dateFin.set(this.getFormattedDate(currentEndDate));
      console.log(this.dateFin());
    }
  }
}
